import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { CampoDoPasso, JanelaEmBlocos } from './PassoAPasso.tsx';
import { usarFormulario } from '../lib/formulario.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { maiuscula } from '../lib/caixa.ts';
import type { ContatoDaProspeccao, DadosDoContato } from '../lib/api.ts';

/**
 * ⭐⭐ COM QUEM FALAR NA EMPRESA — 20/09/2026 (PRD §49.10).
 *
 * > *"Precisa ter a possibilidade de incluir mais de um e-mail, telefone e
 * > sócio/responsável/quem decide."* — Richard
 *
 * ⚠️ **O NOME é opcional, e é ele que separa as duas coisas que cabem aqui:**
 * com nome, é uma pessoa; sem nome, é o canal da empresa — o `contato@`, o
 * telefone do escritório. O levantamento achou os dois, e várias empresas só
 * publicam o segundo. ⛔ O que não pode é ficar sem nome **e** sem nenhuma forma
 * de falar: aí não é contato, é linha vazia.
 */

const TRATAMENTOS = ['Prezados', 'Dr.', 'Dra.'];

export function FormularioContatoDaProspeccao({
  inicial,
  primeiro,
  aoFechar,
  aoSalvar,
}: {
  inicial: ContatoDaProspeccao | null;
  /** É o primeiro contato da empresa? Então ele vira principal sozinho. */
  primeiro: boolean;
  aoFechar: () => void;
  aoSalvar: (dados: DadosDoContato) => Promise<void>;
}) {
  const editando = inicial !== null;
  const [d, setD] = useState<Required<DadosDoContato>>(() => ({
    nome: inicial?.nome ?? '',
    cargo: inicial?.cargo ?? '',
    tratamento: inicial?.tratamento ?? 'Prezados',
    email: inicial?.email ?? '',
    telefone: inicial?.telefone ?? '',
    whatsapp: inicial?.whatsapp ?? '',
    linkedin: inicial?.linkedin ?? '',
    observacao: inicial?.observacao ?? '',
    principal: inicial?.principal ?? primeiro,
  }));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  const guarda = usarFormulario(d, aoFechar);
  usarDialogo(guarda.fechar);

  const muda = <K extends keyof DadosDoContato>(k: K, v: Required<DadosDoContato>[K]) => {
    setErro(null);
    setD((x) => ({ ...x, [k]: v }));
  };

  function enviar(evento: FormEvent | MouseEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    if (!d.nome.trim() && !d.email.trim() && !d.telefone.trim() && !d.whatsapp.trim()) {
      return setErro('Escreva um nome, ou uma forma de falar com este contato.');
    }
    guarda.pedirParaSalvar();
  }

  async function gravar() {
    if (emVoo.current) return;
    guarda.cancelarPendencia();
    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await aoSalvar({ ...d, email: d.email.trim().toLowerCase() });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui salvar.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  return (
    <>
      <JanelaEmBlocos
        rotulo="Contato da empresa"
        sobrelinha={editando ? 'Comercial · editar contato' : 'Comercial · novo contato'}
        titulo={editando ? 'Editar contato' : 'Novo contato'}
        sub="Sem nome, é o contato geral da empresa. Com nome, é a pessoa com quem se fala."
        aoFechar={guarda.fechar}
        erro={erro}
        rodape={
          <>
            <button type="button" className="imv-bt fantasma" onClick={guarda.fechar}>Cancelar</button>
            <span className="imv-esp" />
            <button type="button" className="imv-bt primario" disabled={salvando} onClick={(e) => void enviar(e)}>
              <Icone nome="check" tamanho={14} /> {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="imv-grade-campos">
          <CampoDoPasso rotulo="Nome" col={3} nota="Vazio é o contato geral da empresa.">
            <input value={d.nome} onChange={(e) => muda('nome', maiuscula(e.target.value))} maxLength={200} autoFocus />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Cargo" col={2}>
            <input value={d.cargo} onChange={(e) => muda('cargo', maiuscula(e.target.value))} maxLength={160} />
          </CampoDoPasso>
          {/* ⛔ Onde a fonte não publica, fica Prezados: errar o tratamento de
              quem decide custa mais que a formalidade. */}
          <CampoDoPasso rotulo="Tratamento" col={1}>
            <select value={d.tratamento} onChange={(e) => muda('tratamento', e.target.value)}>
              {TRATAMENTOS.map((tx) => (
                <option key={tx} value={tx}>{tx}</option>
              ))}
            </select>
          </CampoDoPasso>

          <CampoDoPasso rotulo="E-mail" col={3} mono>
            <input type="email" value={d.email} onChange={(e) => muda('email', e.target.value)} maxLength={200} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="WhatsApp" col={2} mono>
            <input value={d.whatsapp} onChange={(e) => muda('whatsapp', e.target.value)} maxLength={60} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Telefone" col={1} mono>
            <input value={d.telefone} onChange={(e) => muda('telefone', e.target.value)} maxLength={60} />
          </CampoDoPasso>

          <CampoDoPasso rotulo="LinkedIn" col={3} mono>
            <input value={d.linkedin} onChange={(e) => muda('linkedin', e.target.value)} maxLength={240} />
          </CampoDoPasso>
          <CampoDoPasso rotulo="Observação" col={3} nota="O que ajuda a falar com esta pessoa.">
            <input value={d.observacao} onChange={(e) => muda('observacao', e.target.value)} maxLength={600} />
          </CampoDoPasso>

          <div className="imv-campo c6">
            <label className="fp-marca">
              <input type="checkbox" checked={d.principal} onChange={(e) => muda('principal', e.target.checked)} />
              <span>
                <strong>É com quem falar primeiro.</strong> É daqui que o texto de abordagem tira o tratamento e o
                vocativo. {primeiro && !editando ? 'Como é o primeiro contato, já nasce assim.' : 'Um por empresa: marcar este desmarca o outro.'}
              </span>
            </label>
          </div>
        </div>
      </JanelaEmBlocos>

      <GuardaDoFormulario
        pendencia={guarda.pendencia}
        oQue="o contato"
        aoCancelar={guarda.cancelarPendencia}
        aoSalvar={() => void gravar()}
        aoDescartar={guarda.aoFecharDeVerdade}
      />
    </>
  );
}
